import * as yup from 'yup';
import { nanoid } from 'nanoid';
import Ability from '../interfaces/ability';
import BaseSchema from 'yup/lib/schema';
import FieldTypes from '../enums/field-types';
import moment from 'moment';
import toFixed from 'accounting-js/lib/toFixed.js';
import { FarmUser } from '../interfaces/farm-user';
import { getI18n } from 'react-i18next';
import { PartialAbility } from '../router/abilityContext';
import { CropWorkType } from '../enums/crop-work-types';
import Work from '../interfaces/work';
import Field from '../interfaces/field';

export function accessData(data: any, column: any) {
  let accessor: string = column.accessor.toString();
  let translatable: (key: string) => string = column.translatable;
  let value: any;
  if (accessor.indexOf(',') !== -1) {
    value = accessor
      .split(',')
      .map((accessorPart) =>
        accessorPart.split('.').reduce(function (o, k) {
          return o && o[k];
        }, data)
      )
      .join(' ');
  } else {
    value = accessor.split('.').reduce(function (o, k) {
      return o && o[k];
    }, data);
  }

  if (typeof translatable == 'function') {
    switch (column.dataType) {
      case FieldTypes.MULTI_SELECT_RELATION:
      case FieldTypes.SELECT_RELATION:
        value = value.map((item: any) => translatable(item.label)).sort();
        break;
      case FieldTypes.MULTI_SELECT_VALUE:
        value = value?.map((item: any) => translatable(item)).sort();
        break;
      case FieldTypes.SELECT_VALUE:
      case FieldTypes.DECIMAL:
      case FieldTypes.INTEGER:
      case FieldTypes.TEXT:
      default:
        let translationKey =
          (column.translationPrefix ? column.translationPrefix : '') + value;

        if (
          getI18n().exists(translationKey) ||
          getI18n().options.debug === true
        ) {
          value = translatable(translationKey);
        }
        break;
    }
  }

  if (typeof column.valuable == 'function') {
    switch (column.dataType) {
      case FieldTypes.MULTI_SELECT_RELATION:
      case FieldTypes.SELECT_RELATION:
        value = value
          .map((item: any) => column.valuable(item.label, data))
          .sort();
        break;
      case FieldTypes.MULTI_SELECT_VALUE:
        value = value.map((item: any) => column.valuable(item, data)).sort();
        break;
      case FieldTypes.SELECT_VALUE:
      case FieldTypes.DECIMAL:
      case FieldTypes.INTEGER:
      case FieldTypes.TEXT:
      default:
        value = column.valuable(value, data);
        break;
    }
  }

  return value;
}

type errorMessage = {
  message: string;
  type: string;
  [key: string]: errorMessage | any;
};

export function parseError(name: string, errors: any) {
  const path = name.split('.').filter((item) => item);

  const step = (path: string[], err: any): errorMessage | null => {
    if (!path.length) {
      return err;
    }
    return path[0] && err[path[0]] ? step(path.slice(1), err[path[0]]) : null;
  };

  return step(path, errors);
}

export function route(route: string, params: any = {}): string {
  let builtRoute = route;

  Object.keys(params).forEach((property) => {
    builtRoute = builtRoute.replace(`:${property}`, params[property]);
  });

  return builtRoute;
}

/**
 * Add query parameters to route, like route?search=sometextvalue&onlyActive=true.
 * You can use this after call to route, like routeQuery(route(ROUTE_NAME, PATH_PARAMS), QUERY_PARAMS)
 */
export function routeQuery(route: string, params: any = {}): string {
  let builtRoute = route;

  let first = true;
  Object.keys(params).forEach((property) => {
    const value = params[property];

    if (Array.isArray(value)) {
      // For array values, append each entry with the index in square brackets (e.g., field_name[0])
      value.forEach((item, index) => {
        builtRoute = builtRoute.concat(
          first ? '?' : '&',
          `${property}[${index}]`,
          '=',
          encodeURIComponent(item)
        );
        first = false;
      });
    } else {
      // For non-array values, append as usual
      builtRoute = builtRoute.concat(
        first ? '?' : '&',
        property,
        '=',
        encodeURIComponent(value)
      );
      first = false;
    }
  });

  return builtRoute;
}

export function can(
  farmUser: FarmUser,
  ability: PartialAbility = null,
  params: any = {}
): boolean {
  if (ability == null) {
    return true;
  }

  if (!farmUser || !farmUser.abilities) {
    return false;
  }

  const abilityObject = ability instanceof Function ? ability(params) : ability;

  const keys = Object.keys(abilityObject);
  const matchedAbilities = farmUser.abilities.find((item: Ability) =>
    keys.every(
      (key) =>
        item[key as keyof PartialAbility] ===
        abilityObject[key as keyof PartialAbility]
    )
  );

  return !!matchedAbilities;
}

export function uuid(): string {
  return nanoid(36); // Generates a default 36-character ID
}

export function numberTransform(v: string): string | null {
  return v === '' || Number.isNaN(v) ? null : v;
}

// Round number to precision
// round(0.5) === 1
// round(-0.5) === -1
// round(0.615, 2) === 0.62
// round(-0.615, 2) === -0.62
// round(1.005, 2) === 1.01
// round(-1.005, 2) === -1.01
export function round(number: number, precision: number = 0): number {
  return Number(toFixed(Math.abs(number), precision)) * Math.sign(number);
}

export function when(
  callback: (value: any, parent: any) => boolean,
  trueSchema: BaseSchema,
  falseSchema: BaseSchema
) {
  return yup.lazy(function (value, resolveOptions) {
    if (callback(value, resolveOptions?.parent)) {
      return trueSchema;
    }

    return falseSchema;
  });
}

export function uniqueValues(o: any[], key: string = 'id') {
  return o.filter((item, index) => {
    return o.findIndex((obj) => obj[key] === item[key]) === index;
  });
}

export function isObject(o: any): boolean {
  return typeof o === 'object' && o !== null && !Array.isArray(o);
}

export function deepCopy(o: object) {
  return JSON.parse(JSON.stringify(o));
}

export function downloadFile(file: Blob, name: string): void {
  const fileURL = window.URL.createObjectURL(new Blob([file]));
  const fileLink = document.createElement('a');
  fileLink.href = fileURL;
  fileLink.setAttribute('download', name);
  document.body.appendChild(fileLink);
  fileLink.click();
}

export function periodsDiffInDays(dateTill: Date, dateFrom: Date) {
  return (
    moment(dateTill)
      .startOf('day')
      .diff(moment(dateFrom).startOf('day'), 'days') + 1
  );
}

export function isValidDateSting(dateString: any): boolean {
  return !isNaN(Date.parse(dateString));
}

export function objectProperty(object: any, key: string): any {
  return key.split('.').reduce((a, b) => a?.[b], object);
}

export function findNewestSowingBeforeOtherWorks(formData: any): Work[] {
  // Filter sowing works
  const sowingWorks = formData.fields.flatMap((field: Field) =>
    field.works // Check if field.works is defined
      ? field.works.filter(
          (work: Work) =>
            work.type_id === CropWorkType.SOWING_OR_PLANTING &&
            !work.products[0].is_interculture
        )
      : []
  );

  // Filter other works that are done and have cargos
  const otherWorks = formData.fields.flatMap((field: Field) =>
    field.works // Check if field.works is defined
      ? field.works.filter(
          (work: Work) =>
            [
              CropWorkType.THRESHING,
              CropWorkType.FORAGE_HARVESTING,
              CropWorkType.FRUIT_HARVESTING,
              CropWorkType.VEGETABLE_ETC_HARVESTING,
            ].includes(work.type_id) &&
            work.done &&
            work.cargos.length > 0
        )
      : []
  );

  // Map other works to find the newest sowing work before each other work
  const newestSowingBeforeOtherWorks = otherWorks.map((otherWork: Work) => {
    // Filter sowing works that occurred before or on the same date as the current other work
    const sowingBeforeOtherWork = sowingWorks.filter(
      (sowingWork: Work) =>
        new Date(sowingWork.date) <= new Date(otherWork.date)
    );

    // Find the newest sowing work for the current other work
    const newestSowingWork = sowingBeforeOtherWork.reduce(
      (prevWork, currentWork) => {
        const prevCreatedAt = new Date(prevWork.date);
        const currentCreatedAt = new Date(currentWork.date);
        return prevCreatedAt > currentCreatedAt ? prevWork : currentWork;
      },
      {} as Work
    );

    return newestSowingWork;
  });

  return newestSowingBeforeOtherWorks;
}

export function formatDateValue(date: string): string {
  const formattedDate = moment(date, moment.ISO_8601, true);

  if (!formattedDate.isValid()) {
    return date;
  }

  return formattedDate.format('DD.MM.YYYY');
}

export function formatDecimalValue(value: number): string | number {
  const numValue = Number(value);

  if (isNaN(numValue)) {
    return value;
  }

  return !isNaN(numValue) && numValue % 1 === 0 ? numValue : numValue.toFixed(2);
}
